/**
 * Utility functions for API calls
 */

import type { ApiResponse, ErrorResponse } from '@/lib/types/api';

interface FetchOptions extends RequestInit {
  requireAuth?: boolean;
}

let logoutCallback: (() => void) | null = null;

export const setLogoutCallback = (callback: () => void) => {
  logoutCallback = callback;
};

/**
 * Enhanced fetch function with error handling and authentication
 */
export async function fetchApi<T>(
  url: string,
  options: FetchOptions = { requireAuth: true }
): Promise<ApiResponse<T>> {
  const { requireAuth = true, ...fetchOptions } = options;

  const headers = new Headers(options.headers);

  if (requireAuth) {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Session expired. Please login again');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Only set Content-Type if body is not FormData
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle 401 Unauthorized error
    if (response.status === 401) {
      // Call logout if available
      if (logoutCallback) {
        logoutCallback();
      }
      const data = await response.json();
      const { error } = data as ErrorResponse;
      throw new Error(
        error.errorMessage || 'Session expired. Please login again.'
      );
    }

    const data = await response.json();

    if (!response.ok) {
      const { error } = data as ErrorResponse;
      throw new Error(error.errorMessage || 'An error occurred');
    }

    return data as ApiResponse<T>;
  } catch (error) {
    if (error instanceof Error) {
      // Add more context to network errors
      if (error.message.includes('fetch')) {
        throw new Error(`Network error: ${error.message}`);
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Check if a response is an error response
 */
export function isErrorResponse(response: unknown): response is ErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'message' in response &&
    'status' in response
  );
}
